import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isHandledError } from "@/lib/api/errors";
import { ChevronRight, Loader2, Plus, X } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { useToast } from "@/hooks/use-toast";
import { MATERIALITIES } from "@/data/materialsAndCategories";
import {
  useCreatePlan,
  useDeletePlan,
  useMaterialityPlanning,
  useUpdatePlan,
} from "@/hooks/api/useMaterialityPlanning";
import type { MaterialityPlanningData } from "@/services/materiality-planning.service";

// ─── Constants ───────────────────────────────────────────────────────────────

const MESES_SHORT = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 11 }, (_, i) => CURRENT_YEAR + i);

// ─── Types ────────────────────────────────────────────────────────────────────

type PlanValue = { id: number; materialityId: number; materialityName: string };
type LocalChange = PlanValue | "deleted";
type PlanMap = Map<string, PlanValue>;

// ─── Utilities ────────────────────────────────────────────────────────────────

function cellKey(
  year: number,
  month: number,
  catId: number,
  isMain: boolean,
): string {
  return `${year}|${month}|${catId}|${isMain ? 1 : 0}`;
}

function buildPlanMap(data: MaterialityPlanningData | undefined): PlanMap {
  const map = new Map<string, PlanValue>();
  if (!data) return map;
  for (const yd of data.years) {
    for (const md of yd.months) {
      for (const cat of md.categories) {
        for (const item of cat.items) {
          map.set(cellKey(yd.year, md.month, cat.category_id, item.is_main), {
            id: item.materiality_plan_id,
            materialityId: item.materiality_id,
            materialityName: item.materiality_name,
          });
        }
      }
    }
  }
  return map;
}

function generateColumns(
  startYear: number,
  startMonth: number,
  endYear: number,
  endMonth: number,
): { year: number; month: number }[] {
  const cols: { year: number; month: number }[] = [];
  let y = startYear;
  let m = startMonth;
  while (y < endYear || (y === endYear && m <= endMonth)) {
    cols.push({ year: y, month: m });
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }
  return cols;
}

// ─── Color system ─────────────────────────────────────────────────────────────

type ColorParts = { bg: string; text: string; border: string };

const COLORS: Record<string, ColorParts> = {
  "banho":                            { bg: "bg-[#A7F3D0]", text: "text-[#064E3B]", border: "border-[#6EE7B7]" },
  "brochura":                         { bg: "bg-[#BFDBFE]", text: "text-[#1E3A8A]", border: "border-[#93C5FD]" },
  "capa dura":                        { bg: "bg-[#FECACA]", text: "text-[#7F1D1D]", border: "border-[#FCA5A5]" },
  "pano":                             { bg: "bg-[#E9D5FF]", text: "text-[#4C1D95]", border: "border-[#D8B4FE]" },
  "cartonado":                        { bg: "bg-[#FDE68A]", text: "text-[#7C2D12]", border: "border-[#FCD34D]" },
  "cartonado sem interatividade":     { bg: "bg-[#FDE68A]", text: "text-[#7C2D12]", border: "border-[#FCD34D]" },
  "cartonado recortes progressivos":  { bg: "bg-[#FDBA74]", text: "text-[#7C2D12]", border: "border-[#FB923C]" },
  "cartonado recortes":               { bg: "bg-[#FCA5A5]", text: "text-[#7F1D1D]", border: "border-[#F87171]" },
  "cartonado abas":                   { bg: "bg-[#93C5FD]", text: "text-[#1E3A8A]", border: "border-[#60A5FA]" },
  "cartonado quebra-cabeça":          { bg: "bg-[#D1FAE5]", text: "text-[#065F46]", border: "border-[#6EE7B7]" },
  "cartonado disco giratório":        { bg: "bg-[#DDD6FE]", text: "text-[#4C1D95]", border: "border-[#C4B5FD]" },
  "cartonado formas rotativas":       { bg: "bg-[#FBCFE8]", text: "text-[#831843]", border: "border-[#F9A8D4]" },
  "cartonado deslize":                { bg: "bg-[#BBF7D0]", text: "text-[#166534]", border: "border-[#86EFAC]" },
  "cartonado apito":                  { bg: "bg-[#FFE4E6]", text: "text-[#9F1239]", border: "border-[#FECDD3]" },
  "cartonado tabs":                   { bg: "bg-[#CFFAFE]", text: "text-[#164E63]", border: "border-[#A5F3FC]" },
  "cartonado toque e sinta":          { bg: "bg-[#F5D0FE]", text: "text-[#701A75]", border: "border-[#E879F9]" },
  "cartonado puxe e empurre":         { bg: "bg-[#EDE9FE]", text: "text-[#4C1D95]", border: "border-[#C4B5FD]" },
  "cartonado puxe e ache":            { bg: "bg-[#FEF9C3]", text: "text-[#713F12]", border: "border-[#FEF08A]" },
  "cartonado pop-up":                 { bg: "bg-[#FEE2E2]", text: "text-[#7F1D1D]", border: "border-[#FECACA]" },
  "cartonado sanfonado":              { bg: "bg-[#ECFEFF]", text: "text-[#164E63]", border: "border-[#CFFAFE]" },
  "cartonado trilhas":                { bg: "bg-[#D1FAE5]", text: "text-[#064E3B]", border: "border-[#A7F3D0]" },
  "cartonado chocalho":               { bg: "bg-[#FEF3C7]", text: "text-[#78350F]", border: "border-[#FDE68A]" },
  "cartonado dedoches":               { bg: "bg-[#E0F2FE]", text: "text-[#0C4A6E]", border: "border-[#BAE6FD]" },
};

const FALLBACK_COLOR: ColorParts = {
  bg: "bg-[#F3FAFB]",
  text: "text-[#135045]",
  border: "border-[#88C0B2]",
};

function getColor(name: string): ColorParts {
  const key = name.toLowerCase();
  const exact = COLORS[key];
  if (exact) return exact;
  if (key.startsWith("cartonado")) return COLORS["cartonado"];
  return FALLBACK_COLOR;
}

// ─── Materiality dropdown ─────────────────────────────────────────────────────

const nonCartonado = MATERIALITIES.filter(
  (m) => !m.name.toLowerCase().startsWith("cartonado"),
);
const cartonadoItems = MATERIALITIES.filter((m) =>
  m.name.toLowerCase().startsWith("cartonado"),
);

interface MatMenuProps {
  onSelect: (id: number, name: string) => void;
  onClose: () => void;
}

const MatMenu = memo(function MatMenu({ onSelect, onClose }: MatMenuProps) {
  const [showSub, setShowSub] = useState(false);

  return (
    <div className="w-52 bg-[#E8EFF0] rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.18)] py-1.5 z-[60]">
      {nonCartonado.map((m) => (
        <button
          key={m.id}
          type="button"
          className="h-10 w-full px-3 text-sm text-left text-[#151C1D] flex items-center hover:bg-[#00000015] transition-colors"
          onClick={() => {
            onSelect(m.id, m.name);
            onClose();
          }}
        >
          {m.name}
        </button>
      ))}
      <div className="relative">
        <button
          type="button"
          className={`h-10 w-full px-3 text-sm text-[#151C1D] flex items-center justify-between transition-colors ${showSub ? "bg-[#00000015]" : "hover:bg-[#00000015]"}`}
          onClick={() => setShowSub((v) => !v)}
        >
          <span>Cartonado</span>
          <ChevronRight
            className={`w-4 h-4 text-[#3B494A] transition-transform ${showSub ? "rotate-90" : ""}`}
          />
        </button>
        {showSub && (
          <div className="absolute left-full top-0 ml-1 w-60 bg-[#E8EFF0] rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.18)] py-1.5 z-[70] max-h-72 overflow-y-auto">
            {cartonadoItems.map((m) => (
              <button
                key={m.id}
                type="button"
                className="h-10 w-full px-3 text-sm text-left text-[#151C1D] flex items-center hover:bg-[#00000015] transition-colors"
                onClick={() => {
                  onSelect(m.id, m.name);
                  onClose();
                }}
              >
                <span className="capitalize truncate">
                  {m.name.replace(/^cartonado\s*/i, "")}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

// ─── Cell ─────────────────────────────────────────────────────────────────────

interface MatCellProps {
  year: number;
  month: number;
  categoryId: number;
  isMain: boolean;
  item: PlanValue | undefined;
  isPending: boolean;
  onAssign: (
    year: number,
    month: number,
    catId: number,
    isMain: boolean,
    matId: number,
    matName: string,
  ) => void;
  onRemove: (
    year: number,
    month: number,
    catId: number,
    isMain: boolean,
    planId: number,
  ) => void;
}

const MatCell = memo(function MatCell({
  year,
  month,
  categoryId,
  isMain,
  item,
  isPending,
  onAssign,
  onRemove,
}: MatCellProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const color = item ? getColor(item.materialityName) : null;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (isPending) {
    return (
      <div className="h-[80px] rounded-xl border border-[#BAC9CA] bg-[#F7FAFB] flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-[#00646B]" />
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={`h-[80px] rounded-xl border p-2 flex flex-col items-center justify-center relative ${
        item
          ? `${color!.bg} ${color!.border}`
          : "border-dashed border-[#C2D1D2] bg-[#F7FAFB]"
      }`}
    >
      {item ? (
        <>
          <span
            className={`text-[11px] font-semibold text-center leading-tight line-clamp-2 ${color!.text}`}
          >
            {item.materialityName}
          </span>
          <div className="flex items-center gap-1 mt-1.5">
            <button
              type="button"
              className={`h-5 px-2 rounded-md text-[10px] border border-current/25 bg-white/30 hover:bg-white/50 transition-colors ${color!.text}`}
              onClick={() => setOpen((v) => !v)}
            >
              Trocar
            </button>
            <button
              type="button"
              className={`h-5 w-5 rounded-md border border-current/25 bg-white/30 hover:bg-white/50 transition-colors ${color!.text} flex items-center justify-center`}
              onClick={() => onRemove(year, month, categoryId, isMain, item.id)}
              title="Remover"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </>
      ) : (
        <button
          type="button"
          className="flex items-center justify-center text-[#9BAEAF] hover:text-[#00646B] transition-colors p-1"
          onClick={() => setOpen((v) => !v)}
          title="Adicionar materialidade"
        >
          <Plus className="w-5 h-5" />
        </button>
      )}

      {open && (
        <div className="absolute top-full left-0 mt-1 z-[55]">
          <MatMenu
            onSelect={(matId, matName) => {
              setOpen(false);
              onAssign(year, month, categoryId, isMain, matId, matName);
            }}
            onClose={() => setOpen(false)}
          />
        </div>
      )}
    </div>
  );
});

// ─── Main component ───────────────────────────────────────────────────────────

export default function MaterialityCalendar() {
  const { toast } = useToast();

  const [startMonth, setStartMonth] = useState(1);
  const [startYear, setStartYear] = useState(CURRENT_YEAR);
  const [endMonth, setEndMonth] = useState(12);
  const [endYear, setEndYear] = useState(CURRENT_YEAR);
  const [rangeInitialized, setRangeInitialized] = useState(false);

  const { data: apiData, isLoading } = useMaterialityPlanning();

  // Auto-set range from API response on first load
  useEffect(() => {
    if (rangeInitialized || !apiData || apiData.years.length === 0) return;

    let minYear = Infinity, minMonth = 13;
    let maxYear = -Infinity, maxMonth = 0;

    for (const yd of apiData.years) {
      for (const md of yd.months) {
        if (yd.year < minYear || (yd.year === minYear && md.month < minMonth)) {
          minYear = yd.year;
          minMonth = md.month;
        }
        if (yd.year > maxYear || (yd.year === maxYear && md.month > maxMonth)) {
          maxYear = yd.year;
          maxMonth = md.month;
        }
      }
    }

    if (Number.isFinite(minYear)) {
      setStartYear(minYear);
      setStartMonth(minMonth);
    }
    if (Number.isFinite(maxYear)) {
      setEndYear(maxYear);
      setEndMonth(maxMonth);
    }
    setRangeInitialized(true);
  }, [apiData, rangeInitialized]);

  // Clamp end so it's never before start
  const effectiveEndYear =
    endYear < startYear ? startYear : endYear;
  const effectiveEndMonth =
    effectiveEndYear === startYear && endMonth < startMonth
      ? startMonth
      : endMonth;

  const columns = useMemo(
    () => generateColumns(startYear, startMonth, effectiveEndYear, effectiveEndMonth),
    [startYear, startMonth, effectiveEndYear, effectiveEndMonth],
  );

  const createPlan = useCreatePlan();
  const updatePlan = useUpdatePlan();
  const deletePlan = useDeletePlan();

  // Base plan map derived from server data
  const baseMap = useMemo(() => buildPlanMap(apiData), [apiData]);

  // Categories come directly from the API response — always present even when items is []
  const categories = useMemo(() => {
    if (!apiData) return [];
    const seen = new Map<number, string>();
    for (const yd of apiData.years) {
      for (const md of yd.months) {
        for (const cat of md.categories) {
          if (!seen.has(cat.category_id)) {
            seen.set(cat.category_id, cat.category_code);
          }
        }
        if (seen.size > 0) break; // one month is enough — structure is consistent
      }
      if (seen.size > 0) break;
    }
    return [...seen.entries()]
      .map(([id, code]) => ({ id, code }))
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [apiData]);

  // Optimistic overlay: cells being mutated before server round-trip
  const [localChanges, setLocalChanges] = useState<Map<string, LocalChange>>(
    new Map(),
  );
  const [pendingCells, setPendingCells] = useState<Set<string>>(new Set());

  // Remove local overrides once the server data has caught up
  useEffect(() => {
    if (localChanges.size === 0) return;
    const toRemove: string[] = [];
    for (const [key, change] of localChanges) {
      if (change === "deleted") {
        if (!baseMap.has(key)) toRemove.push(key);
      } else {
        const base = baseMap.get(key);
        if (base && base.materialityId === change.materialityId) {
          toRemove.push(key);
        }
      }
    }
    if (toRemove.length === 0) return;
    setLocalChanges((prev) => {
      const next = new Map(prev);
      for (const k of toRemove) next.delete(k);
      return next;
    });
  }, [baseMap]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effective map = server data merged with optimistic changes
  const planMap = useMemo(() => {
    const map = new Map(baseMap);
    for (const [key, change] of localChanges) {
      if (change === "deleted") map.delete(key);
      else map.set(key, change);
    }
    return map;
  }, [baseMap, localChanges]);

  // Column headers: show year label only when it changes
  const columnMeta = useMemo(
    () =>
      columns.map(({ year, month }, idx) => ({
        year,
        month,
        showYear: idx === 0 || columns[idx - 1].year !== year,
      })),
    [columns],
  );

  const handleAssign = useCallback(
    async (
      year: number,
      month: number,
      catId: number,
      isMain: boolean,
      matId: number,
      matName: string,
    ) => {
      const key = cellKey(year, month, catId, isMain);
      const existing = planMap.get(key);

      setPendingCells((prev) => new Set(prev).add(key));
      setLocalChanges((prev) => {
        const next = new Map(prev);
        next.set(key, {
          id: existing?.id ?? 0,
          materialityId: matId,
          materialityName: matName,
        });
        return next;
      });

      try {
        if (existing) {
          await updatePlan.mutateAsync({
            id: existing.id,
            body: {
              materiality_id: matId,
              category_id: catId,
              month,
              year,
              is_main: isMain,
            },
          });
        } else {
          const result = await createPlan.mutateAsync({
            materiality_id: matId,
            category_id: catId,
            month,
            year,
            is_main: isMain,
          });
          setLocalChanges((prev) => {
            const next = new Map(prev);
            next.set(key, {
              id: result.materiality_plan_id,
              materialityId: matId,
              materialityName: matName,
            });
            return next;
          });
        }
      } catch (err) {
        setLocalChanges((prev) => {
          const next = new Map(prev);
          next.delete(key);
          return next;
        });
        if (!isHandledError(err)) toast({ title: "Erro ao salvar", variant: "destructive" });
      } finally {
        setPendingCells((prev) => {
          const n = new Set(prev);
          n.delete(key);
          return n;
        });
      }
    },
    [planMap, createPlan, updatePlan, toast],
  );

  const handleRemove = useCallback(
    async (
      year: number,
      month: number,
      catId: number,
      isMain: boolean,
      planId: number,
    ) => {
      const key = cellKey(year, month, catId, isMain);
      setPendingCells((prev) => new Set(prev).add(key));
      setLocalChanges((prev) => {
        const next = new Map(prev);
        next.set(key, "deleted");
        return next;
      });

      try {
        await deletePlan.mutateAsync(planId);
      } catch (err) {
        setLocalChanges((prev) => {
          const next = new Map(prev);
          next.delete(key);
          return next;
        });
        if (!isHandledError(err)) toast({ title: "Erro ao remover", variant: "destructive" });
      } finally {
        setPendingCells((prev) => {
          const n = new Set(prev);
          n.delete(key);
          return n;
        });
      }
    },
    [deletePlan, toast],
  );

  return (
    <div className="flex h-screen bg-white">
      <Navigation />

      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 z-50 border-b border-[#BAC9CA] bg-white">
          <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-4">
            <h1 className="text-[24px] font-medium text-[#151C1D]">
              Calendário de Materialidades
            </h1>

            {/* Range selector */}
            <div className="flex items-center gap-3">
              {/* Start */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-[#3B494A] whitespace-nowrap">De</span>
                <select
                  value={startMonth}
                  onChange={(e) => setStartMonth(Number(e.target.value))}
                  className="h-9 rounded-lg border border-[#BAC9CA] bg-[#EDF4F5] px-2 text-sm text-[#151C1D] focus:outline-none focus:ring-1 focus:ring-[#00646B]"
                >
                  {MESES_SHORT.map((m, i) => (
                    <option key={i + 1} value={i + 1}>{m}</option>
                  ))}
                </select>
                <select
                  value={startYear}
                  onChange={(e) => setStartYear(Number(e.target.value))}
                  className="h-9 rounded-lg border border-[#BAC9CA] bg-[#EDF4F5] px-2 text-sm text-[#151C1D] focus:outline-none focus:ring-1 focus:ring-[#00646B]"
                >
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              <span className="text-[#3B494A] text-sm">→</span>

              {/* End */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-[#3B494A] whitespace-nowrap">Até</span>
                <select
                  value={endMonth}
                  onChange={(e) => setEndMonth(Number(e.target.value))}
                  className="h-9 rounded-lg border border-[#BAC9CA] bg-[#EDF4F5] px-2 text-sm text-[#151C1D] focus:outline-none focus:ring-1 focus:ring-[#00646B]"
                >
                  {MESES_SHORT.map((m, i) => (
                    <option key={i + 1} value={i + 1}>{m}</option>
                  ))}
                </select>
                <select
                  value={endYear}
                  onChange={(e) => setEndYear(Number(e.target.value))}
                  className="h-9 rounded-lg border border-[#BAC9CA] bg-[#EDF4F5] px-2 text-sm text-[#151C1D] focus:outline-none focus:ring-1 focus:ring-[#00646B]"
                >
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              {columns.length > 0 && (
                <span className="text-xs text-[#6B797A] whitespace-nowrap">
                  {columns.length} {columns.length === 1 ? "mês" : "meses"}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-40 gap-2 text-[#3B494A]">
              <Loader2 className="w-5 h-5 animate-spin text-[#00646B]" />
              <span className="text-sm">Carregando calendário…</span>
            </div>
          ) : categories.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-sm text-[#3B494A]">
              Nenhuma categoria encontrada.
            </div>
          ) : (
            <div
              className="px-5 py-5"
              style={{
                display: "grid",
                gridTemplateColumns: `152px repeat(${columns.length}, 172px)`,
                columnGap: "6px",
                rowGap: "4px",
                minWidth: "max-content",
              }}
            >
              {/* ── Header row ── */}
              {/* Corner */}
              <div className="sticky top-0 left-0 z-50 h-12 bg-white rounded-xl" />
              {/* Month labels */}
              {columnMeta.map(({ year, month, showYear }) => (
                <div
                  key={`h-${year}-${month}`}
                  className="sticky top-0 z-40 h-12 flex flex-col items-center justify-center rounded-xl bg-[#EDF4F5] border border-[#BAC9CA]"
                >
                  <span className="text-xs font-medium text-[#151C1D] leading-none">
                    {MESES_SHORT[month - 1]}
                  </span>
                  {showYear && (
                    <span className="text-[9px] font-semibold text-[#00646B] mt-0.5 leading-none">
                      {year}
                    </span>
                  )}
                </div>
              ))}

              {/* ── Data rows ── */}
              {categories.map((cat, catIdx) => (
                <Fragment key={cat.id}>
                  {/* Horizontal divider between categories */}
                  {catIdx > 0 && (
                    <div
                      style={{
                        gridColumn: `1 / -1`,
                        height: "1px",
                        background: "#D6E5E6",
                        margin: "2px 0",
                      }}
                    />
                  )}

                  {/* Primary row label */}
                  <div
                    className="sticky left-0 z-30 h-[80px] rounded-xl border border-[#BAC9CA] flex flex-col items-center justify-center px-2"
                    style={{
                      background: "linear-gradient(135deg,#E0F2F1 0%,#F0FAF9 100%)",
                    }}
                  >
                    <span className="text-sm font-bold text-[#135045]">
                      {cat.code}
                    </span>
                    <span className="text-[10px] text-[#3B494A] mt-0.5 uppercase tracking-wide font-medium">
                      Primária
                    </span>
                  </div>
                  {/* Primary cells */}
                  {columns.map(({ year, month }) => {
                    const key = cellKey(year, month, cat.id, true);
                    return (
                      <MatCell
                        key={key}
                        year={year}
                        month={month}
                        categoryId={cat.id}
                        isMain={true}
                        item={planMap.get(key)}
                        isPending={pendingCells.has(key)}
                        onAssign={handleAssign}
                        onRemove={handleRemove}
                      />
                    );
                  })}

                  {/* Secondary row label */}
                  <div
                    className="sticky left-0 z-30 h-[80px] rounded-xl border border-[#BAC9CA] flex flex-col items-center justify-center px-2"
                    style={{
                      background: "linear-gradient(135deg,#F5FAFB 0%,#FFFFFF 100%)",
                    }}
                  >
                    <span className="text-sm font-bold text-[#3B6E68]">
                      {cat.code}
                    </span>
                    <span className="text-[10px] text-[#6B797A] mt-0.5 uppercase tracking-wide font-medium">
                      Secundária
                    </span>
                  </div>
                  {/* Secondary cells */}
                  {columns.map(({ year, month }) => {
                    const key = cellKey(year, month, cat.id, false);
                    return (
                      <MatCell
                        key={key}
                        year={year}
                        month={month}
                        categoryId={cat.id}
                        isMain={false}
                        item={planMap.get(key)}
                        isPending={pendingCells.has(key)}
                        onAssign={handleAssign}
                        onRemove={handleRemove}
                      />
                    );
                  })}
                </Fragment>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
